import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { useRouter } from 'next/router';

import { createUrlWithSourceQueryParams } from '@crehana/analytics';
import { useCentralizedProviderData, useMe } from '@crehana/auth';
import {
  URL_RIVE_ANIMATIONS_ASSET_CONFETTI_URL,
  useIsPlayRoomActivated,
} from '@crehana/compass.playroom';
import { UIThemeProvider } from '@crehana/compass.ui';
import { PageLoader } from '@crehana/compass.ui/v2';
import { FeatureFlagProvider } from '@crehana/feature-flags/react';
import { useTranslation } from '@crehana/i18n';
import { CrehanaNextSeo, type TAppProps } from '@crehana/next';
import { useIsRenderedFromWebView } from '@crehana/react-hooks/use-is-rendered-from-webview';
import { PrivatePage, useUITheme } from '@crehana/web';

import { B2BRootLayout } from './components/B2BOrgLayout';
import IntercomProviderHooks from './components/LoadChatScripts/IntercomProviderHooks';
import { LEARN_DASHBOARD_PAGE_NAME } from './constants';
import { LearnDashboardProvider } from './context';
import useIsNewSidebarEnabled from './hooks/useIsNewSidebarEnabled';
import useOrganizationTheme from './hooks/useOrganizationTheme';
import LearnDashboard from './main/LearnDashboard';
import type { IAppProps } from './types';
import { changeFavicon } from './utils/changeFavicon';

const App: React.FC<TAppProps<IAppProps>> = ({ organizationSlug }) => {
  const router = useRouter();
  const { me, loading } = useMe();
  const { isDark } = useUITheme();
  const { languageCode } = useTranslation();
  const {
    isActivated: isPlayroomEnabled,
    loading: isLoadingPlayroomActivated,
  } = useIsPlayRoomActivated(organizationSlug);
  const {
    isCrossNavigationSidebarActive,
    loading: loadingIsCrossNavigationSidebarActive,
  } = useIsNewSidebarEnabled(organizationSlug);
  const { loading: loadingCentralizedMe } = useCentralizedProviderData();

  const isB2B = !!me?.isB2B;
  // Don't replace this constant with organizationSlug prop because it's not always available, and it's not same as organizationInfo.slug
  const userOrganizationSlug = me?.organization?.slug;
  const userOrganizationId = me?.organization?.originalId || undefined;

  const shouldFixCurrentB2BUrl =
    isB2B &&
    !!userOrganizationSlug &&
    !!organizationSlug &&
    userOrganizationSlug !== organizationSlug;
  const isLoggedIn = !loading && !!me;
  const shouldRenderContent =
    isLoggedIn && languageCode && !shouldFixCurrentB2BUrl;

  const isFromApps = useIsRenderedFromWebView();
  const { organizationInfo, loading: loadingOrganizationInfo } =
    useOrganizationTheme({
      organizationSlug: organizationSlug!,
      // Skip the query if the user is trying to load other organization.
      skip: shouldFixCurrentB2BUrl,
    });

  /**
   * If the user is logged in and the organization slug is not the same as the one in the URL,
   * redirect to the correct organization.
   */
  useEffect(() => {
    if (shouldFixCurrentB2BUrl) {
      void router.replace(`/org/${userOrganizationSlug}/learn/`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFixCurrentB2BUrl, userOrganizationSlug]);

  /**
   * If the user is not logged in, redirect to the login page.
   */
  useEffect(() => {
    if (!loading && !me) {
      if (organizationSlug) {
        const b2bLoginUrl = `/org/${organizationSlug}/entrar/?next=${router.asPath}`;

        void router.replace(b2bLoginUrl);
      } else {
        const loginUrl = `/entrar/?next=${router.asPath}`;

        void router.replace(loginUrl);
      }
    } else if (me && !me.isB2B) {
      // If the user is logged in but is not a B2B, redirect to the B2C Home User.
      const homeB2C_URL = createUrlWithSourceQueryParams({
        url: `/home/`,
        source: LEARN_DASHBOARD_PAGE_NAME,
        sourceDetail: 'Auto Redirect',
      });

      void router.replace(homeB2C_URL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, me, organizationSlug]);

  const renderPageContent = () => {
    if (!shouldRenderContent) {
      return null;
    }

    if (
      loadingOrganizationInfo ||
      loadingIsCrossNavigationSidebarActive ||
      loadingCentralizedMe
    ) {
      return <PageLoader size="s" languageCode={languageCode} />;
    }

    const organizationTheme = {
      primaryColor: organizationInfo?.lookAndFeel?.primary?.main || undefined,
      secondaryColor:
        organizationInfo?.lookAndFeel?.secondary?.main || undefined,
    };

    return (
      <LearnDashboardProvider
        me={me}
        isDark={isDark}
        organizationSlug={organizationSlug}
        organizationId={userOrganizationId}
        organizationTheme={organizationTheme}
      >
        <IntercomProviderHooks
          organizationInfo={organizationInfo}
          shouldInitialize={!isFromApps}
        >
          <LearnDashboard
            isPlayroomEnabled={isPlayroomEnabled}
            isCrossNavigationSidebarActive={isCrossNavigationSidebarActive}
            isLoadingPlayroomActivated={isLoadingPlayroomActivated}
            organizationInfo={organizationInfo}
          />
        </IntercomProviderHooks>
      </LearnDashboardProvider>
    );
  };

  useEffect(() => {
    if (organizationInfo?.favicon) {
      changeFavicon(organizationInfo.favicon);
    }
  }, [organizationInfo]);

  return (
    <>
      <CrehanaNextSeo
        title="Learn Dashboard"
        description="Certifícate, alcanza tus objetivos y mejora tus habilidades con nuestros packs de cursos con más de 40% de descuento. ¡Aprende hoy y aplica hoy!"
        hasCountryCode={false}
        nofollow
        noindex
        additionalLinkTags={[
          {
            rel: 'preload',
            href: URL_RIVE_ANIMATIONS_ASSET_CONFETTI_URL,
          },
        ]}
      />

      <PrivatePage
        isDark={isDark}
        languageCode={languageCode}
        organizationSlug={organizationSlug}
      >
        <B2BRootLayout
          isB2B={isB2B}
          organizationSlug={organizationSlug}
          organizationInfo={organizationInfo}
        />

        {renderPageContent()}
      </PrivatePage>
    </>
  );
};

const withProviders = (Component: React.FC<TAppProps<IAppProps>>) => {
  const Providers = (props: TAppProps<IAppProps>) => {
    const { me, loading } = useMe();
    const { languageCode } = useTranslation();

    if (loading) {
      return <PageLoader size="s" languageCode={languageCode} />;
    }

    const organizationId = me?.organization?.originalId || undefined;

    return (
      <UIThemeProvider>
        <BrowserRouter>
          <FeatureFlagProvider
            flags={[
              ['user_notification_web', 'V2'],
              ['CERTIFICATION_WITH_QUIZZES', 'V5'],
              ['LEARNING_QUIZZES_MASSIFICATION', 'V5'],
              ['NEW_SIDEBAR_LAYOUT', 'V5'],
              ['LEARNING_MIGRATE_USER_CERTIFICATE_QUERY', 'V5'],
              ['LEARNING_HIDE_BANNER_DOWNLOAD_APP', 'V5'],
              ['SIDEBAR_V2', 'V5'],
            ]}
            organizationId={organizationId!}
          >
            <Component {...props} />
          </FeatureFlagProvider>
        </BrowserRouter>
      </UIThemeProvider>
    );
  };

  Providers.displayName = 'App';

  return Providers;
};

export default withProviders(App);
